// The lot's contact slots (BL-2748) — one schema for both sides, per ADR 016:
// the lot editor's Contacts section and `PATCH /api/lots/[id]` read the same
// fields. A lot has three fixed slots in a fixed order (Sales rep, Seller, the
// optional Additional contact), so there is no list, no row id and no ordering
// to validate: three booleans and four free-text fields.
//
// BL-2783: a shown Additional ("other") contact must carry a phone or an email,
// the same test the bidder resolver reads (`isReachableContact`), so a contact
// that saves is a contact the lot page shows. The rule rides each schema's
// `.superRefine(...)` through `addOtherContactReachableIssue`.

import { isReachableContact } from '@auctogon/core/contact';
import { z } from '@auctogon/core/zod';

import { contactEmail, contactText } from './contact-person';
import { addPhoneFormatIssue } from './phone-field';

/**
 * The lot's contact-slot fields, every one optional so a PATCH that omits them
 * leaves the columns alone and the item door (which has no lot yet) carries none.
 * Spread into `LotPatchRequestSchema` and `ItemFormSchema`.
 */
export const LotContactSlotFields = {
  /** Show the lot's sales rep (lot rep, else seller's rep, else the auction contact). */
  showSalesRep: z.boolean().optional(),
  /** Show the seller of record. */
  showSeller: z.boolean().optional(),
  /** Show the Additional contact below. */
  showAdditionalContact: z.boolean().optional(),
  additionalContactTitle: contactText(60, 'Title is too long.').optional(),
  additionalContactName: contactText(200, 'Name is too long.').optional(),
  additionalContactPhone: contactText(50, 'Phone is too long.').optional(),
  additionalContactEmail: contactEmail().optional(),
};

/** The lot's stored other contact, as the PATCH route reads it before parsing. */
export interface PersistedOtherContact {
  showAdditionalContact: boolean;
  additionalContactPhone: string | null;
  additionalContactEmail: string | null;
}

/** Inputs for {@link addAdditionalContactPhoneIssue} and {@link addOtherContactReachableIssue}. */
export interface LotContactRuleOptions {
  /**
   * The Additional contact phone currently stored on the lot: the grandfather
   * clause of `addPhoneFormatIssue` (BL-1277), so a phone already on the lot that
   * the operator did not touch still saves (BUG-2132 AC 2, ADR 066 — Offline
   * Mode: a queued save that never touched it replays). The server reads it from
   * the database before parsing, never from the request.
   */
  persistedAdditionalPhone?: string | null;
  /**
   * BL-2783: the other contact as stored (the server reads it from the database,
   * the editor from its loaded detail). A PATCH names only what it changes, so
   * the rule judges the stored contact with the request laid over it, and a
   * stored contact the request leaves as it is is never refused (ADR 066 —
   * Offline Mode: a queued save that never touched it replays). Absent, the
   * values are judged as given.
   */
  persistedOtherContact?: PersistedOtherContact;
}

/**
 * BUG-2132 AC 2 — refuse a typed Additional contact phone that is not a real
 * number, under that field, with the shared "Enter a valid phone number.".
 * Call it from a schema's `.superRefine(...)`, not from the object shape.
 *
 * @param data - The parsed values (only `additionalContactPhone` is read).
 * @param ctx - The Zod refinement context the issue is added to.
 * @param options - The stored phone. See {@link LotContactRuleOptions}.
 */
export function addAdditionalContactPhoneIssue(
  data: { additionalContactPhone?: string | null },
  ctx: z.RefinementCtx,
  { persistedAdditionalPhone }: LotContactRuleOptions = {},
): void {
  if (data.additionalContactPhone === undefined) return;
  addPhoneFormatIssue(data.additionalContactPhone, ctx, {
    path: ['additionalContactPhone'],
    persistedValue: persistedAdditionalPhone,
  });
}

/** The other contact's Phone hint, and its error when neither Phone nor Email is filled. */
export const OTHER_CONTACT_UNREACHABLE_MESSAGE = 'Enter a phone or email.';

/**
 * BL-2783 AC 4 / AC 5 / AC 11 — refuse a shown other contact with neither a phone
 * nor an email, under Phone. A Title or a Name alone does not count: the bidder
 * lot page would never show the contact. Call it from a schema's `.superRefine(...)`.
 *
 * @param data - The parsed values (the show flag, phone and email are read).
 * @param ctx - The Zod refinement context the issues are added to.
 * @param options - The stored other contact. See {@link LotContactRuleOptions}.
 */
export function addOtherContactReachableIssue(
  data: Pick<LotContactSlotsInput, 'showAdditionalContact' | 'additionalContactPhone' | 'additionalContactEmail'>,
  ctx: z.RefinementCtx,
  { persistedOtherContact: stored }: LotContactRuleOptions = {},
): void {
  const merged = {
    showAdditionalContact: data.showAdditionalContact ?? stored?.showAdditionalContact ?? false,
    additionalContactPhone: data.additionalContactPhone !== undefined ? data.additionalContactPhone : (stored?.additionalContactPhone ?? null),
    additionalContactEmail: data.additionalContactEmail !== undefined ? data.additionalContactEmail : (stored?.additionalContactEmail ?? null),
  };
  if (!merged.showAdditionalContact) return;
  if (isReachableContact({ phone: merged.additionalContactPhone, email: merged.additionalContactEmail })) return;
  if (stored != null && isSameOtherContact(merged, stored)) return;
  // On Phone only, the Add sales rep modal's either-or pattern: the editor marks both fields
  // required and states the rule as Phone's hint, which this error replaces.
  ctx.addIssue({ code: 'custom', path: ['additionalContactPhone'], message: OTHER_CONTACT_UNREACHABLE_MESSAGE });
}

/** Whether the request leaves the stored show flag, phone and email as they are (blank and null are one value). */
function isSameOtherContact(next: PersistedOtherContact, stored: PersistedOtherContact): boolean {
  const norm = (v: string | null) => (v == null || v.trim() === '' ? null : v.trim());
  return next.showAdditionalContact === stored.showAdditionalContact
    && norm(next.additionalContactPhone) === norm(stored.additionalContactPhone)
    && norm(next.additionalContactEmail) === norm(stored.additionalContactEmail);
}

/** The slot fields' keys, for code that moves them between a parsed body and the write. */
export const LOT_CONTACT_SLOT_KEYS = Object.keys(LotContactSlotFields) as ReadonlyArray<keyof typeof LotContactSlotFields>;

/** The lot's contact slots as the form holds and the PATCH sends them. */
export type LotContactSlotsInput = {
  showSalesRep?: boolean;
  showSeller?: boolean;
  showAdditionalContact?: boolean;
  additionalContactTitle?: string | null;
  additionalContactName?: string | null;
  additionalContactPhone?: string | null;
  additionalContactEmail?: string | null;
};
